import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import {
  ISiteFacet,
  IUpdateSiteFacetValue,
  useUpdateSiteFacetMutation,
} from "../../../../redux/api/site-classification/siteClassificationAPI";
import { IHttpError } from "../../../../shared/oversight-core/interfaces/http-errror";
import AppButton from "../../../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import CheckBox from "../../../../shared/oversight-core/ui-elements/check-box/check-box";
import AppInput from "../../../../shared/oversight-core/ui-elements/input/app-input";
import ModalContainer from "../../../../shared/oversight-core/ui-elements/modal-container/modal-container";
import {
  showErrorMessage,
  showSuccessMessage,
} from "../../../../shared/oversight-core/utils/toast";

interface IProps {
  show: boolean;
  tenantId: string;
  facet?: ISiteFacet;
  onClose: () => void;
  onConflict: () => void;
}

interface IDraft {
  name: string;
  hiddenFromGuests: boolean;
  archived: boolean;
  values: IUpdateSiteFacetValue[];
}

const draftOf = (facet?: ISiteFacet): IDraft => ({
  name: facet?.name ?? "",
  hiddenFromGuests: facet?.hiddenFromGuests ?? false,
  archived: facet?.archived ?? false,
  values: (facet?.values ?? []).map((value) => ({ ...value })),
});

const sameLabel = (first: string, second: string) =>
  first.trim().toLowerCase() === second.trim().toLowerCase();

// Edits one facet: its name, whether guests see it, whether it is archived, and its picklist.
// Values are archived rather than removed, since sites may already carry them.
const EditSiteFacetModal = (props: IProps) => {
  const { show, tenantId, facet, onClose, onConflict } = props;
  const [draft, setDraft] = useState<IDraft>(draftOf(facet));
  const [newValue, setNewValue] = useState("");
  const [updateSiteFacet, { isLoading }] = useUpdateSiteFacetMutation();

  useEffect(() => {
    if (show) {
      setDraft(draftOf(facet));
      setNewValue("");
    }
  }, [show, facet]);

  if (!facet) {
    return null;
  }

  const isDuplicate =
    newValue.trim() !== "" &&
    draft.values.some((value) => sameLabel(value.label, newValue));

  const addValue = () => {
    if (newValue.trim() === "" || isDuplicate) {
      return;
    }

    setDraft((current) => ({
      ...current,
      values: [
        ...current.values,
        { label: newValue.trim(), archived: false },
      ],
    }));
    setNewValue("");
  };

  const updateValue = (index: number, change: Partial<IUpdateSiteFacetValue>) =>
    setDraft((current) => ({
      ...current,
      values: current.values.map((value, position) =>
        position === index ? { ...value, ...change } : value,
      ),
    }));

  const removeNewValue = (index: number) =>
    setDraft((current) => ({
      ...current,
      values: current.values.filter((_, position) => position !== index),
    }));

  const save = () => {
    updateSiteFacet({
      tenantId,
      facetId: facet.id,
      name: draft.name,
      hiddenFromGuests: draft.hiddenFromGuests,
      archived: draft.archived,
      values: draft.values,
    })
      .unwrap()
      .then(() => {
        showSuccessMessage(`${draft.name.trim()} saved`);
        onClose();
      })
      .catch((error: IHttpError) => {
        showErrorMessage(error?.message || "The facet could not be saved.");
        // 400 means someone else saved the facet first: start again from what is stored now. A
        // rule this edit broke (412) keeps the edit, so it can be corrected.
        if (error?.status === 400) {
          onConflict();
        }
      });
  };

  const hasEmptyLabel = draft.values.some((value) => value.label.trim() === "");

  return (
    <ModalContainer
      show={show}
      onClose={onClose}
      onCancel={onClose}
      onConfirm={save}
      title={`Edit ${facet.name}`}
      confirmButtonText="Save"
      confirmButtonVariant="blue"
      size="modal-md"
      isLoading={isLoading}
      disabled={draft.name.trim() === "" || hasEmptyLabel}
    >
      <div className="d-flex flex-column gap-3">
        <AppInput
          name="facetName"
          label="Name"
          placeholder="Facet name"
          value={draft.name}
          onChange={(event) =>
            setDraft((current) => ({ ...current, name: event.target.value }))
          }
          errors={{}}
        />
        <div className="text-light font-size-12">
          {facet.cardinality === "SINGLE"
            ? "A site takes one value"
            : "A site can take several values"}
          {facet.required ? " · required for every site" : ""}
        </div>
        <CheckBox
          label="Hide from monitoring guests"
          checked={draft.hiddenFromGuests}
          onChange={(checked) =>
            setDraft((current) => ({ ...current, hiddenFromGuests: checked }))
          }
        />
        <CheckBox
          label={
            facet.required
              ? "Archive (not possible: every site needs this facet)"
              : "Archive this facet"
          }
          checked={draft.archived}
          disabled={facet.required}
          onChange={(checked) =>
            setDraft((current) => ({ ...current, archived: checked }))
          }
        />
        <div className="text-dark font-weight-500 font-size-14 mt-2">
          Values
        </div>
        {draft.values.length === 0 && (
          <div className="text-light font-size-13">No values yet.</div>
        )}
        {draft.values.map((value, index) => (
          <Row key={value.id ?? `new-${index}`} className="align-items-center">
            <Col>
              <AppInput
                name={`value-${index}`}
                placeholder="Value"
                value={value.label}
                onChange={(event) =>
                  updateValue(index, { label: event.target.value })
                }
                errors={{}}
              />
            </Col>
            <Col className="col-auto">
              {value.id ? (
                <CheckBox
                  label="Archived"
                  checked={value.archived}
                  onChange={(checked) => updateValue(index, { archived: checked })}
                />
              ) : (
                <AppButton
                  text="Remove"
                  size="extra-small"
                  variant="transparentWithBorder"
                  onClick={() => removeNewValue(index)}
                />
              )}
            </Col>
          </Row>
        ))}
        <Row className="align-items-center">
          <Col>
            <AppInput
              name="newValue"
              placeholder="Add a value, e.g. Hospital"
              value={newValue}
              onChange={(event) => setNewValue(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  addValue();
                }
              }}
              errors={{}}
            />
          </Col>
          <Col className="col-auto">
            <AppButton
              text="Add"
              size="small"
              variant="blue"
              onClick={addValue}
              disabled={newValue.trim() === "" || isDuplicate}
            />
          </Col>
        </Row>
        {isDuplicate && (
          <div className="text-danger font-size-12">
            {newValue.trim()} is already a value of {facet.name}.
          </div>
        )}
      </div>
    </ModalContainer>
  );
};

export default EditSiteFacetModal;
